import React from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import { css } from "glamor";

import {
    getJsonSchemaFromPath,
    getTitleFromPath,
    getValueFromPath,
    deepExtractObjectPaths,
    setObj,
} from "../util";

import { formatWithJsonSchema } from "../formatters";

const ObjectViewer = ({
    data,
    ignoreEmpty,
    className,
    withoutLabels,
    schema,
    styles,
    inline,
}) => {
    const tbodyStyles = inline ? { ...styles.inline } : {};
    return (
        <div
            {...styles.viewer}
            className={getValueFromPath("object", className)}
        >
            <div {...tbodyStyles}>
                {R.keys(data).map(key => {
                    if (ignoreEmpty && !data[key]) {
                        return null;
                    }

                    if (R.equals(key, "@type")) {
                        return null;
                    }
                    const childSchema = getJsonSchemaFromPath(key, schema);
                    const value = getValueFromPath(key, data);
                    const title = getTitleFromPath(key, schema);

                    return (
                        <div
                            key={key}
                            className={getValueFromPath("infoItem", className)}
                        >
                            {!withoutLabels && (
                                <span className={styles.title}>
                                    {title}:&nbsp;
                                </span>
                            )}
                            <span
                                className={getValueFromPath(
                                    "infoValue",
                                    className
                                )}
                            >
                                <GenericViewerValue
                                    ignoreEmpty={ignoreEmpty}
                                    data={value}
                                    className={className}
                                    withoutLabels={withoutLabels}
                                    schema={childSchema}
                                    styles={styles}
                                />
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
/**
 * Flattens nested data structures
 * into key value table
 *
 * TODO make this deal with arrays a bit more elegantly
 */
const FlattenedListViewer = ({
    data,
    ignoreEmpty,
    className,
    schema,
    withoutLabels,
    styles,
    inline,
}) => {
    const paths = deepExtractObjectPaths(data);
    const tbodyStyles = inline ? { ...styles.inline } : {};
    return (
        <div
            {...styles.viewer}
            className={getValueFromPath("object", className)}
        >
            <div {...tbodyStyles}>
                {paths.map(path => {
                    const title = getTitleFromPath(path, schema);
                    const value = getValueFromPath(path, data);

                    if (ignoreEmpty && !value) {
                        return null;
                    }
                    if (R.equals(key, "@type")) {
                        return null;
                    }
                    const key = R.last(path.split("."));
                    const isArray = key.match(/^\d/);
                    const keyTitle = isArray ? "-" : title;
                    return (
                        <div
                            key={path}
                            className={getValueFromPath("infoItem", className)}
                        >
                            {!withoutLabels && (
                                <span className={styles.title}>
                                    {keyTitle}:&nbsp;
                                </span>
                            )}
                            <span
                                className={getValueFromPath(
                                    "infoValue",
                                    className
                                )}
                            >
                                <GenericViewerValue
                                    ignoreEmpty={ignoreEmpty}
                                    data={value}
                                    className={className}
                                    schema={{}}
                                    styles={styles}
                                />
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ArrayViewer = ({
    data,
    ignoreEmpty,
    className,
    withoutLabels,
    schema,
    styles,
}) => {
    const childSchema = getJsonSchemaFromPath(0, schema);
    if (data.length === 1) {
        if (ignoreEmpty && !data[0]) {
            return null;
        }
        return (
            <GenericViewerValue
                data={data[0]}
                className={className}
                ignoreEmpty={ignoreEmpty}
                withoutLabels={withoutLabels}
                schema={childSchema}
                styles={styles}
            />
        );
    } else {
        return (
            <ul
                className={getValueFromPath("array", className)}
                {...styles.list}
            >
                {data.map((item, i) => {
                    if (ignoreEmpty && !item) {
                        return null;
                    }
                    return (
                        <li key={i}>
                            <GenericViewerValue
                                ignoreEmpty={ignoreEmpty}
                                data={item}
                                className={className}
                                withoutLabels={withoutLabels}
                                schema={childSchema}
                                styles={styles}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    }
};

const getFilteredObject = (data, limitToPaths) => {
    if (!limitToPaths || limitToPaths.length < 1) {
        return data;
    }
    return limitToPaths.reduce((out, path) => {
        const value = getValueFromPath(path, data);
        return setObj(out, path, value);
    }, {});
};

const GenericViewerValue = props => {
    const { className, limitToPaths, flatten } = props;
    const data = getFilteredObject(props.data, limitToPaths);
    const render = data => (
        <span className={getValueFromPath("element", className)}>{data}</span>
    );
    const newProps = Object.assign({}, props, { data });
    if (typeof data === "boolean") {
        return render(data ? "Yes" : "No");
    } else if (React.isValidElement(data)) {
        return render(data);
    } else if (R.type(data) === "Object") {
        if (flatten) {
            return <FlattenedListViewer {...newProps} />;
        }
        return <ObjectViewer {...newProps} />;
    } else if (R.type(data) === "Array") {
        return <ArrayViewer {...newProps} />;
    } else {
        return render(data);
    }
};

const formatPropsWithSchema = props => {
    const { schema } = props;
    if (!schema) return props;
    return {
        ...props,
        data: formatWithJsonSchema({ jsonSchema: schema, data: props.data }),
    };
};

const GenericViewer = props => {
    const formattedProps = formatPropsWithSchema(props);
    return <GenericViewerValue {...formattedProps} />;
};

GenericViewer.defaultProps = {
    flatten: false,
    ignoreEmpty: false,
    withoutLabels: false,
    schema: {},
    styles: {
        viewer: css({}),
        inline: css({}),
        title: css({
            fontWeight: "bold",
        }),
        list: css({
            listStyleType: "none",
            paddingLeft: "0",
        }),
    },
};

GenericViewer.propTypes = {
    className: PropTypes.object,
    ignoreEmpty: PropTypes.bool,
    data: PropTypes.any.isRequired,
    schema: PropTypes.object,
    // flatten nested objects
    // into one list
    flatten: PropTypes.bool,
    // use JSON schema to format data
    // correctly
    styles: PropTypes.object,
    withoutLabels: PropTypes.bool,
    // filter data for these paths
    // on the object
    limitToPaths: PropTypes.array,
};
export default GenericViewer;
